import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SignIn, SignOut, FirstAid, Pill, Plus, Bell, Lock, CheckCircle, NotePencil } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { StudentAvatar } from "@/components/StudentAvatar";

const STATE = { in: ["Checked in", "bg-emerald-50 text-emerald-700 border-emerald-200"],
                out: ["Checked out", "bg-slate-100 text-slate-600 border-slate-200"],
                absent: ["Absent", "bg-red-50 text-red-600 border-red-200"],
                none: ["Not arrived", "bg-amber-50 text-amber-700 border-amber-200"] };
const MED_TONE = { upcoming: "bg-slate-100 text-slate-700", due: "bg-amber-100 text-amber-800",
                   overdue: "bg-red-100 text-red-700", given: "bg-emerald-100 text-emerald-800",
                   missed: "bg-slate-200 text-slate-700", refused: "bg-slate-200 text-slate-700",
                   held: "bg-slate-200 text-slate-700", not_applicable: "bg-slate-200 text-slate-700" };
const today = () => new Date().toISOString().slice(0, 10);

export default function TeacherTablet() {
  const { logout } = useAuth();
  const [day, setDay] = useState(today());
  const [room, setRoom] = useState("");
  const [data, setData] = useState(null);
  const [alert, setAlert] = useState(null);
  const [note, setNote] = useState(null);
  const [dose, setDose] = useState(null);
  const [newReminder, setNewReminder] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/teacher/workspace", { params: { day, ...(room ? { room } : {}) } });
      setData(d);
      if (!room && d.room) setRoom(d.room);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [day, room]);
  useEffect(() => { load(); }, [load]);

  const record = async (student_id, kind) => {
    setSaving(true);
    try {
      await api.post("/attendance", { student_id, kind });
      await load();
      toast.success(kind === "in" ? "Checked in and saved" : "Checked out and saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const saveNote = async () => {
    setSaving(true);
    try {
      await api.post("/activities", { student_id: note.id, type: note.type, detail: note.detail, staff_only: false });
      await load();
      toast.success("Saved to the daily report");
      setNote(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const acknowledge = async (item) => {
    setSaving(true);
    try {
      const { data: r } = await api.post("/medications/acknowledge",
        { plan_id: item.plan_id, scheduled_key: item.scheduled_key });
      await load();
      toast.info(r.message);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const recordDose = async () => {
    setSaving(true);
    try {
      await api.post("/medications/administrations", {
        plan_id: dose.plan_id, scheduled_key: dose.scheduled_key, outcome: dose.outcome,
        actual_time: dose.actual_time, dose_given: dose.dose_given, notes: dose.notes, follow_up: dose.follow_up });
      await load();
      toast.success("Administration recorded");
      setDose(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const saveReminder = async () => {
    setSaving(true);
    try {
      await api.post("/reminders", { ...newReminder, room: newReminder.forRoom ? room : "" });
      await load();
      toast.success("Reminder saved — it will be here on any device");
      setNewReminder(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const completeReminder = async (id) => {
    try {
      await api.patch(`/reminders/${id}`, { status: "done" });
      await load();
      toast.success("Reminder updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!data) return <p className="text-muted-foreground" data-testid="tablet-loading">Loading your classroom…</p>;

  return (
    <div data-testid="teacher-tablet">
      <div className="flex flex-wrap gap-4 items-end justify-between">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Classroom workspace</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">{data.room || "No classroom assigned"}</h1>
          <p className="mt-2 text-sm text-muted-foreground font-semibold">
            {data.present} present · {data.roster.length} on the roster · times {data.time_zone_label}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div><Label className="text-xs font-bold">Date</Label>
            <Input type="date" value={day} onChange={(e) => setDay(e.target.value)}
                   className="mt-1 h-12 rounded-xl w-44 text-base" data-testid="tablet-date" /></div>
          {data.rooms.length > 1 && (
            <div><Label className="text-xs font-bold">Classroom</Label>
              <Select value={room} onValueChange={setRoom}>
                <SelectTrigger className="mt-1 h-12 rounded-xl w-52 text-base" data-testid="tablet-room"><SelectValue /></SelectTrigger>
                <SelectContent>{data.rooms.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <Button variant="outline" onClick={async () => { await logout(); window.location.assign("/login"); }}
                  className="h-12 rounded-full font-bold" data-testid="tablet-signout">
            <Lock size={18} className="mr-2" /> Sign out
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Present now", data.present, "bg-emerald-50 text-emerald-800"],
          ["Safety alerts", data.safety_alerts, "bg-red-50 text-red-700"],
          ["Medication due", data.medications.filter((m) => m.state === "due").length, "bg-amber-50 text-amber-800"],
          ["Overdue", data.medications_overdue, "bg-red-50 text-red-700"]].map(([k, v, tone]) => (
          <div key={k} className={`rounded-2xl border border-border p-5 ${tone}`}
               data-testid={`tablet-stat-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-xs uppercase tracking-wider font-bold">{k}</p>
            <p className="mt-1 font-display text-3xl font-bold">{v}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white rounded-3xl border border-border p-5 sm:p-6" data-testid="tablet-medications">
        <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Pill size={20} /> Medication reminders</h2>
        <p className="text-xs text-muted-foreground mt-1 font-semibold">
          Acknowledging a reminder does not record that the medication was given. Record each administration separately.
        </p>
        {data.medications.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="tablet-medications-empty">
            No scheduled medications for this classroom today.
          </p>
        ) : (
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {data.medications.map((m) => (
              <div key={`${m.plan_id}-${m.scheduled_key}`} className="rounded-2xl border border-border p-4"
                   data-testid={`med-item-${m.plan_id}-${m.scheduled_time}`}>
                <div className="flex flex-wrap gap-2 items-center">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${MED_TONE[m.state] || "bg-muted"}`}
                        data-testid={`med-state-${m.plan_id}`}>{m.state_label}</span>
                  <p className="font-bold">{m.scheduled_time} · {m.student_name}</p>
                  {m.escalated && <span className="text-xs font-bold text-red-600">Escalated</span>}
                </div>
                <p className="mt-2 text-sm font-semibold">{m.medication} · {m.dose} · {m.route}</p>
                {m.instructions && <p className="mt-1 text-sm text-muted-foreground">{m.instructions}</p>}
                {m.storage && <p className="mt-1 text-xs text-muted-foreground">Storage: {m.storage}</p>}
                <p className="mt-1 text-xs text-muted-foreground">Authorization v{m.authorization.version}
                  {m.authorization.authorized_by ? ` · ${m.authorization.authorized_by}` : ""}</p>
                {m.acknowledged && (
                  <p className="mt-2 text-xs font-bold text-blue-700" data-testid={`med-ack-${m.plan_id}`}>
                    Reminder acknowledged by {m.acknowledged.by} — not yet recorded as given
                  </p>
                )}
                {m.administration ? (
                  <p className="mt-2 text-sm font-bold text-emerald-700" data-testid={`med-recorded-${m.plan_id}`}>
                    {m.administration.outcome_label} by {m.administration.staff_name}
                    {m.administration.notes ? ` · ${m.administration.notes}` : ""}
                  </p>
                ) : (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="lg" variant="outline" disabled={saving} onClick={() => acknowledge(m)}
                            className="h-12 rounded-full font-bold" data-testid={`med-acknowledge-${m.plan_id}`}>
                      <Bell size={16} className="mr-2" /> Acknowledge
                    </Button>
                    <Button size="lg" disabled={saving}
                            onClick={() => setDose({ ...m, outcome: "given", actual_time: "", dose_given: m.dose, notes: "", follow_up: "" })}
                            className="h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                            data-testid={`med-record-${m.plan_id}`}>
                      <CheckCircle size={16} className="mr-2" /> Record administration
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 grid gap-4 xl:grid-cols-[1.6fr,1fr]">
        <div className="bg-white rounded-3xl border border-border p-5 sm:p-6" data-testid="tablet-roster">
          <h2 className="font-display text-xl font-semibold">Live roster</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {data.roster.length === 0 && <p className="text-muted-foreground">No active children in this classroom.</p>}
            {data.roster.map((c) => (
              <div key={c.id} className="rounded-2xl border border-border p-4" data-testid={`tablet-child-${c.id}`}>
                <div className="flex gap-3 items-start">
                  <StudentAvatar student={{ first_name: c.name.split(" ")[0], last_name: c.name.split(" ").slice(1).join(" "), photo_path: c.photo_path }} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold truncate">{c.name}</p>
                    <span className={`inline-block mt-1 text-xs font-bold px-2.5 py-1 rounded-full border ${STATE[c.state][1]}`}
                          data-testid={`tablet-state-${c.id}`}>
                      {STATE[c.state][0]}{c.check_in_time ? ` · in ${c.check_in_time}` : ""}
                    </span>
                    {c.sessions > 1 && <span className="ml-1 text-xs font-bold text-blue-700">{c.sessions} sessions today</span>}
                    {c.late_arrival_minutes > 0 && <span className="ml-1 text-xs font-bold text-amber-700">{c.late_arrival_minutes} min late</span>}
                  </div>
                </div>
                {c.safety_alert && c.safety_alert.status !== "none_reported" && (
                  <button onClick={() => setAlert({ ...c.safety_alert, name: c.name, id: c.id })}
                          className={`mt-3 w-full text-left rounded-xl px-3 py-2 text-xs font-bold border ${
                            c.safety_alert.status === "reported"
                              ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}
                          data-testid={`tablet-alert-${c.id}`}>
                    <FirstAid size={13} className="inline mr-1" />
                    {c.safety_alert.status === "reported" ? c.safety_alert.summary : "Allergy status not yet confirmed"}
                  </button>
                )}
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <Button disabled={saving} onClick={() => record(c.id, "in")}
                          className="h-14 rounded-xl bg-[#10B981] hover:bg-emerald-600 font-bold text-base"
                          data-testid={`tablet-checkin-${c.id}`}><SignIn size={20} /></Button>
                  <Button disabled={saving} onClick={() => record(c.id, "out")}
                          className="h-14 rounded-xl bg-[#3B82F6] hover:bg-[#2563EB] font-bold text-base"
                          data-testid={`tablet-checkout-${c.id}`}><SignOut size={20} /></Button>
                  <Button disabled={saving} variant="outline"
                          onClick={() => setNote({ id: c.id, name: c.name, type: "note", detail: "" })}
                          className="h-14 rounded-xl font-bold" data-testid={`tablet-note-${c.id}`}><NotePencil size={20} /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-3xl border border-border p-5 sm:p-6" data-testid="tablet-reminders">
            <div className="flex justify-between items-start gap-3">
              <h2 className="font-display text-xl font-semibold">My reminders</h2>
              <Button size="sm" onClick={() => setNewReminder({ title: "", notes: "", due_at: day, recurrence: "none", forRoom: true, notify: true })}
                      className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="add-reminder-btn">
                <Plus size={15} className="mr-1" /> Add
              </Button>
            </div>
            <div className="mt-4 space-y-2">
              {data.reminders.length === 0 && <p className="text-sm text-muted-foreground">Nothing saved yet.</p>}
              {data.reminders.map((r) => (
                <div key={r.id} className="rounded-2xl border border-border p-3 flex gap-3 items-start"
                     data-testid={`reminder-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm">{r.title}</p>
                    {r.notes && <p className="text-xs text-muted-foreground mt-0.5">{r.notes}</p>}
                    <p className="text-xs text-muted-foreground mt-1 font-semibold">
                      {r.due_at ? new Date(r.due_at).toLocaleString() : "No date"}
                      {r.recurrence !== "none" ? ` · repeats ${r.recurrence}` : ""}{r.room ? ` · ${r.room}` : " · personal"}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => completeReminder(r.id)}
                          className="rounded-full font-bold" data-testid={`reminder-done-${r.id}`}>Done</Button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-border p-5 sm:p-6" data-testid="tablet-day">
            <h2 className="font-display text-xl font-semibold">Today in this room</h2>
            <dl className="mt-3 space-y-2 text-sm">
              {[["Meals", data.room_schedule.meals], ["Nap", data.room_schedule.nap],
                ["Activities", data.room_schedule.activities]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><dt className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{k}</dt>
                  <dd className="font-semibold">{v}</dd></div>
              ))}
            </dl>
            {data.events.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm">
                {data.events.map((e) => <li key={e.id} className="font-semibold">{e.type}: {e.title}</li>)}
              </ul>
            )}
            {data.tasks.length > 0 && (
              <>
                <h3 className="mt-4 font-bold text-sm uppercase tracking-wider text-muted-foreground">My tasks</h3>
                <ul className="mt-2 space-y-1 text-sm" data-testid="tablet-tasks">
                  {data.tasks.map((t) => <li key={t.id} className="font-semibold">{t.due_date} · {t.title}</li>)}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog open={Boolean(alert)} onOpenChange={(o) => !o && setAlert(null)}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="safety-alert-dialog">
          <DialogHeader><DialogTitle className="font-display">Safety alert — {alert?.name}</DialogTitle>
            <DialogDescription>{alert?.label}{alert?.verified_by ? ` · verified by ${alert.verified_by}` : ""}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="font-bold">{alert?.summary}</p>
            {(alert?.instructions || []).map((i, idx) => (
              <div key={`${i.label}-${idx}`} className="rounded-xl bg-muted p-3">
                <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{i.label}{i.trigger ? ` · ${i.trigger}` : ""}</p>
                <p className="text-sm font-semibold mt-0.5 whitespace-pre-wrap">{i.text}</p>
              </div>
            ))}
            {alert?.needs_confirmation && (
              <p className="text-sm font-semibold text-amber-700">
                This child's allergy status has not been confirmed. Ask an administrator to confirm it with the family.
              </p>
            )}
            <Button onClick={async () => {
              try {
                await api.post(`/students/${alert.id}/health/acknowledge`, { note: "Acknowledged on the classroom tablet" });
                toast.success("Acknowledgement recorded");
                setAlert(null);
              } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
            }} className="w-full h-12 rounded-full bg-[#0F172A] hover:bg-slate-800 font-bold"
                    data-testid="acknowledge-alert-btn">I have read these instructions</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(note)} onOpenChange={(o) => !o && setNote(null)}>
        <DialogContent className="rounded-3xl" data-testid="tablet-note-dialog">
          <DialogHeader><DialogTitle className="font-display">Daily note — {note?.name}</DialogTitle>
            <DialogDescription>Saved to today's report for the family.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm font-bold">Type</Label>
              <Select value={note?.type} onValueChange={(v) => setNote({ ...note, type: v })}>
                <SelectTrigger className="mt-1 h-12 rounded-xl" data-testid="tablet-note-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["note", "meal", "nap", "diaper", "kudos", "health", "incident"].map((t) => (
                  <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>))}</SelectContent>
              </Select></div>
            <div><Label className="text-sm font-bold">Details</Label>
              <Textarea value={note?.detail || ""} onChange={(e) => setNote({ ...note, detail: e.target.value })}
                        className="mt-1 rounded-xl" data-testid="tablet-note-detail" /></div>
            <Button onClick={saveNote} disabled={saving} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="tablet-note-save">{saving ? "Saving…" : "Save note"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(dose)} onOpenChange={(o) => !o && setDose(null)}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="dose-dialog">
          <DialogHeader><DialogTitle className="font-display">Record administration</DialogTitle>
            <DialogDescription>{dose?.student_name} · {dose?.medication} · authorized dose {dose?.dose} ({dose?.route})</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label className="text-sm font-bold">Outcome</Label>
              <Select value={dose?.outcome} onValueChange={(v) => setDose({ ...dose, outcome: v })}>
                <SelectTrigger className="mt-1 h-12 rounded-xl" data-testid="dose-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>{[["given", "Given as written"], ["missed", "Missed"], ["refused", "Refused by child"],
                  ["held", "Held on written instruction"], ["not_applicable", "Not applicable today"]].map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>))}</SelectContent>
              </Select></div>
            <div><Label className="text-sm font-bold">Actual time</Label>
              <Input type="time" value={dose?.actual_time || ""} onChange={(e) => setDose({ ...dose, actual_time: e.target.value })}
                     className="mt-1 h-12 rounded-xl" data-testid="dose-time" /></div>
            <div><Label className="text-sm font-bold">What was given</Label>
              <Input value={dose?.dose_given || ""} onChange={(e) => setDose({ ...dose, dose_given: e.target.value })}
                     className="mt-1 h-12 rounded-xl" data-testid="dose-given" /></div>
            <div className="sm:col-span-2"><Label className="text-sm font-bold">
              Notes {dose?.outcome !== "given" && <span className="text-[#EF4444]">* reason required</span>}</Label>
              <Textarea value={dose?.notes || ""} onChange={(e) => setDose({ ...dose, notes: e.target.value })}
                        className="mt-1 rounded-xl" data-testid="dose-notes" /></div>
            <div className="sm:col-span-2"><Label className="text-sm font-bold">Follow-up</Label>
              <Input value={dose?.follow_up || ""} onChange={(e) => setDose({ ...dose, follow_up: e.target.value })}
                     className="mt-1 h-12 rounded-xl" data-testid="dose-followup" /></div>
          </div>
          <Button onClick={recordDose} disabled={saving} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                  data-testid="dose-save">{saving ? "Saving…" : "Save record"}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(newReminder)} onOpenChange={(o) => !o && setNewReminder(null)}>
        <DialogContent className="rounded-3xl" data-testid="reminder-dialog">
          <DialogHeader><DialogTitle className="font-display">New reminder</DialogTitle>
            <DialogDescription>Saved in your account — it appears after you sign in on any device.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm font-bold">Title</Label>
              <Input value={newReminder?.title || ""} onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })}
                     className="mt-1 h-12 rounded-xl" data-testid="reminder-title" /></div>
            <div><Label className="text-sm font-bold">Notes</Label>
              <Textarea value={newReminder?.notes || ""} onChange={(e) => setNewReminder({ ...newReminder, notes: e.target.value })}
                        className="mt-1 rounded-xl" data-testid="reminder-notes" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-sm font-bold">Due</Label>
                <Input type="date" value={(newReminder?.due_at || "").slice(0, 10)}
                       onChange={(e) => setNewReminder({ ...newReminder, due_at: e.target.value })}
                       className="mt-1 h-12 rounded-xl" data-testid="reminder-due" /></div>
              <div><Label className="text-sm font-bold">Repeat</Label>
                <Select value={newReminder?.recurrence} onValueChange={(v) => setNewReminder({ ...newReminder, recurrence: v })}>
                  <SelectTrigger className="mt-1 h-12 rounded-xl" data-testid="reminder-recurrence"><SelectValue /></SelectTrigger>
                  <SelectContent>{[["none", "Does not repeat"], ["daily", "Every day"], ["weekdays", "Weekdays"],
                    ["weekly", "Weekly"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={Boolean(newReminder?.forRoom)}
                     onChange={(e) => setNewReminder({ ...newReminder, forRoom: e.target.checked })}
                     data-testid="reminder-for-room" /> Share with everyone in {room || "this classroom"}
            </label>
            <Button onClick={saveReminder} disabled={saving || !newReminder?.title}
                    className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="reminder-save">{saving ? "Saving…" : "Save reminder"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
